import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Input, Modal, Form, message } from "antd";
import {
  SearchOutlined, PlusOutlined, EditOutlined, StopOutlined,
  PhoneOutlined, TeamOutlined, ShopOutlined, UserOutlined, HistoryOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { useCustomersCatalog } from "../../../hooks/useCatalogData";
import { apiClient } from "../../../shared/api/AllApi";
import { useTranslation } from "react-i18next";

const PosSubPeople = ({
  onBack,
  vendors,
  onVendorMutated,
  onViewClientStatement,
  onViewVendorStatement,
  canCustomers = true,
  canFarmers = true,
  canAddCustomer = true,
  canAddFarmer = true,
  canEditCustomer = true,
  canDeleteCustomer = true,
  canEditFarmer = true,
  canDeleteFarmer = true,
}) => {
  const { t } = useTranslation();
  const [tab, setTab] = useState(() => (canCustomers ? "customers" : "vendors"));
  const [showDeactivated, setShowDeactivated] = useState(false);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [restoring, setRestoring] = useState(null);
  const [deactivatedCustomers, setDeactivatedCustomers] = useState([]);
  const [deactivatedVendors, setDeactivatedVendors] = useState([]);
  const [loadingDeactivated, setLoadingDeactivated] = useState(false);
  const [form] = Form.useForm();
  const { data: customers, refresh: refreshCustomers } = useCustomersCatalog();

  useEffect(() => {
    if (tab === "customers" && !canCustomers && canFarmers) setTab("vendors");
    if (tab === "vendors" && !canFarmers && canCustomers) setTab("customers");
  }, [tab, canCustomers, canFarmers]);

  const isCustomers = tab === "customers";
  const canEdit = isCustomers ? canEditCustomer : canEditFarmer;
  const canDelete = isCustomers ? canDeleteCustomer : canDeleteFarmer;
  const canManageDeactivated = canEdit || canDelete;

  useEffect(() => {
    if (!canManageDeactivated && showDeactivated) setShowDeactivated(false);
  }, [canManageDeactivated, showDeactivated]);

  const loadDeactivated = useCallback(async () => {
    setLoadingDeactivated(true);
    try {
      const tasks = [];
      if (canCustomers) tasks.push(apiClient.get("/customers/deactivated").then((cr) => setDeactivatedCustomers(Array.isArray(cr.data) ? cr.data : [])));
      if (canFarmers) tasks.push(apiClient.get("/vendors/deactivated").then((vr) => setDeactivatedVendors(Array.isArray(vr.data) ? vr.data : [])));
      await Promise.all(tasks);
    } catch {
      message.error(t("posPeopleLoadDeactivatedFailed", "Failed to load deactivated records"));
    } finally {
      setLoadingDeactivated(false);
    }
  }, [canCustomers, canFarmers, t]);

  useEffect(() => {
    if (showDeactivated) loadDeactivated();
  }, [showDeactivated, loadDeactivated]);

  const handleRestore = async (item) => {
    setRestoring(item.id);
    try {
      const url = isCustomers ? `/customers/${item.id}/restore` : `/vendors/${item.id}/restore`;
      await apiClient.put(url);
      message.success(
        isCustomers
          ? t("posPeopleCustomerRestored", "Customer reactivated")
          : t("posPeopleVendorRestored", "Vendor reactivated")
      );
      if (isCustomers) {
        setDeactivatedCustomers((p) => p.filter((c) => c.id !== item.id));
        await refreshCustomers();
      } else {
        setDeactivatedVendors((p) => p.filter((v) => v.id !== item.id));
        onVendorMutated();
      }
    } catch {
      message.error(t("posPeopleRestoreFailed", "Failed to reactivate"));
    } finally {
      setRestoring(null);
    }
  };

  const filteredCustomers = useMemo(() => {
    const t = search.toLowerCase();
    if (!t) return customers;
    return customers.filter(
      (c) => (c.name || "").toLowerCase().includes(t) || (c.phone_number || c.phone || "").includes(t)
    );
  }, [customers, search]);

  const filteredVendors = useMemo(() => {
    const t = search.toLowerCase();
    if (!t) return vendors;
    return vendors.filter(
      (v) => (v.name || "").toLowerCase().includes(t) || (v.phone || "").includes(t)
    );
  }, [vendors, search]);

  const list = isCustomers ? filteredCustomers : filteredVendors;

  const openAdd = () => {
    setEditItem(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setEditItem(item);
    form.setFieldsValue({
      name: item.name || "",
      phone_number: item.phone_number || item.phone || "",
    });
    setModalOpen(true);
  };

  const handleSave = async (values) => {
    setSaving(true);
    try {
      if (isCustomers) {
        if (editItem) {
          await apiClient.put(`/customers/${editItem.id}`, {
            name: values.name,
            phone_number: values.phone_number || "",
          });
          message.success(t("posPeopleCustomerUpdated", "Customer updated"));
        } else {
          await apiClient.post("/customers", {
            name: values.name,
            phone_number: values.phone_number || "",
          });
          message.success(t("posPeopleCustomerAdded", "Customer added"));
        }
        await refreshCustomers();
      } else {
        if (editItem) {
          await apiClient.put(`/vendors/${editItem.id}`, {
            name: values.name,
            phone: values.phone_number || "",
          });
          message.success(t("posPeopleVendorUpdated", "Vendor updated"));
        } else {
          await apiClient.post("/vendors", {
            name: values.name,
            phone: values.phone_number || "",
          });
          message.success(t("posPeopleVendorAdded", "Vendor added"));
        }
        onVendorMutated();
      }
      setModalOpen(false);
      form.resetFields();
      setEditItem(null);
    } catch {
      message.error(t("posFailedSave", "Failed to save"));
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (item) => {
    Modal.confirm({
      title: isCustomers
        ? t("posPeopleDeactivateCustomerTitle", "Deactivate customer?")
        : t("posPeopleDeactivateVendorTitle", "Deactivate vendor?"),
      content: t(
        "posPeopleDeactivateContent",
        "\"{{name}}\" will be hidden from POS dropdowns. You can restore them later from the main app.",
        { name: item.name }
      ),
      okText: t("posPeopleDeactivateOk", "Deactivate"),
      cancelText: t("cancel", "Cancel"),
      okButtonProps: { danger: true },
      zIndex: 4000,
      onOk: async () => {
        setDeleting(item.id);
        try {
          if (isCustomers) {
            await apiClient.delete(`/customers/${item.id}`);
            await refreshCustomers();
            message.success(t("posPeopleCustomerDeactivated", "Customer deactivated — hidden from POS"));
          } else {
            await apiClient.delete(`/vendors/${item.id}`);
            onVendorMutated();
            message.success(t("posPeopleVendorDeactivated", "Vendor deactivated — hidden from POS"));
          }
        } catch {
          message.error(t("posPeopleDeactivateFailed", "Failed to deactivate"));
        } finally {
          setDeleting(null);
        }
      },
    });
  };

  const addLabel = isCustomers
    ? t("addCustomer", "Add Customer")
    : t("posPeopleAddVendor", "Add Vendor");
  const emptyTitle = isCustomers
    ? t("posPeopleNoCustomers", "No customers yet")
    : t("posPeopleNoVendors", "No vendors / farmers yet");
  const emptySubtitle = isCustomers
    ? t("posPeopleNoCustomersSub", "Add your first customer to start tracking sales")
    : t("posPeopleNoVendorsSub", "Add your farmers and suppliers here");

  return (
    <div className="pos-sub-view">
      <div className="pos-sub-header">
        <div className="pos-sub-header-left">
          <div className="pos-sub-icon"><TeamOutlined /></div>
          <h2 className="pos-sub-title">{t("posNavPeople", "People Management")}</h2>
        </div>
        <button type="button" className="pos-sub-back" onClick={onBack}>
          {t("posBackToPOS", "Back to POS")}
        </button>
      </div>

      <div className="pos-sub-tabs">
        {canCustomers && (
          <button
            type="button"
            className={`pos-sub-tab tab-customers${tab === "customers" ? " active" : ""}`}
            onClick={() => { setTab("customers"); setSearch(""); }}
          >
            <ShopOutlined /> {t("customers", "Customers")}
            <span className="pos-sub-tab-badge">{customers.length}</span>
          </button>
        )}
        {canFarmers && (
          <button
            type="button"
            className={`pos-sub-tab tab-vendors${tab === "vendors" ? " active" : ""}`}
            onClick={() => { setTab("vendors"); setSearch(""); }}
          >
            <UserOutlined /> {t("vendorsMenu", "Vendors / Farmers")}
            <span className="pos-sub-tab-badge">{vendors.length}</span>
          </button>
        )}
      </div>

      <div className="pos-sub-stats">
        {canCustomers && (
          <div className="pos-sub-stat pos-sub-stat-customers">
            <div className="pos-sub-stat-icon"><ShopOutlined /></div>
            <div className="pos-sub-stat-body">
              <div className="pos-sub-stat-num">{customers.length}</div>
              <div className="pos-sub-stat-lbl">{t("customers", "Customers")}</div>
            </div>
          </div>
        )}
        {canFarmers && (
          <div className="pos-sub-stat pos-sub-stat-vendors">
            <div className="pos-sub-stat-icon"><UserOutlined /></div>
            <div className="pos-sub-stat-body">
              <div className="pos-sub-stat-num">{vendors.length}</div>
              <div className="pos-sub-stat-lbl">{t("vendorsMenu", "Vendors / Farmers")}</div>
            </div>
          </div>
        )}
      </div>

      <div className="pos-sub-toolbar">
        {!showDeactivated && (
          <Input
            prefix={<SearchOutlined />}
            placeholder={isCustomers
              ? t("posPeopleSearchCustomers", "Search customers...")
              : t("posPeopleSearchVendors", "Search vendors / farmers...")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            allowClear
            className="pos-sub-search"
          />
        )}
        {!showDeactivated && ((isCustomers && canAddCustomer) || (!isCustomers && canAddFarmer)) && (
          <button type="button" className="pos-sub-add-btn" onClick={openAdd}>
            <PlusOutlined /> {addLabel}
          </button>
        )}
        {canManageDeactivated && (
          <button
            type="button"
            onClick={() => { setShowDeactivated((v) => !v); setSearch(""); }}
            style={{
              height: 36, padding: "0 14px", borderRadius: 8, border: "1.5px solid",
              borderColor: showDeactivated ? "#f59e0b" : "#e2e8f0",
              background: showDeactivated ? "#fffbeb" : "#fff",
              color: showDeactivated ? "#d97706" : "#64748b",
              fontWeight: 700, fontSize: 12, cursor: "pointer",
              display: "flex", alignItems: "center", gap: 6, flexShrink: 0,
              whiteSpace: "nowrap",
            }}
          >
            <StopOutlined /> {showDeactivated
              ? t("active", "Active")
              : t("posPeopleDeactivated", "Deactivated")}
          </button>
        )}
      </div>

      {showDeactivated ? (
        <div className="pos-sub-list">
          {loadingDeactivated ? (
            <div className="pos-sub-empty"><div className="pos-sub-empty-title">{t("posLoading", "Loading...")}</div></div>
          ) : (isCustomers ? deactivatedCustomers : deactivatedVendors).length === 0 ? (
            <div className="pos-sub-empty">
              <div className="pos-sub-empty-icon"><CheckCircleOutlined style={{ color: "#0891b2" }} /></div>
              <div className="pos-sub-empty-title">
                {isCustomers
                  ? t("posPeopleNoDeactivatedCustomers", "No deactivated customers")
                  : t("posPeopleNoDeactivatedVendors", "No deactivated vendors")}
              </div>
            </div>
          ) : (
            <div className="pos-sub-cards">
              {(isCustomers ? deactivatedCustomers : deactivatedVendors).map((item) => (
                <div
                  className={`pos-sub-card ${isCustomers ? "card-customer" : "card-vendor"}`}
                  key={item.id}
                  style={{ opacity: 0.75, borderLeft: "3px solid #f59e0b" }}
                >
                  <div className="pos-sub-card-avatar" style={{ background: "#f59e0b" }}>
                    {(item.name || "?")[0].toUpperCase()}
                  </div>
                  <div className="pos-sub-card-info">
                    <div className="pos-sub-card-name">{item.name}</div>
                    {(item.phone_number || item.phone) && (
                      <div className="pos-sub-card-phone">
                        <PhoneOutlined /> {item.phone_number || item.phone}
                      </div>
                    )}
                    <span style={{ fontSize: 10, color: "#d97706", fontWeight: 700 }}>
                      {t("posPeopleDeactivated", "Deactivated").toUpperCase()}
                    </span>
                  </div>
                  <div className="pos-sub-card-actions">
                    {canEdit && (
                      <button
                        type="button"
                        className="pos-sub-add-btn"
                        style={{ height: 34, padding: "0 10px", fontSize: 12, borderRadius: 7, display: "flex", alignItems: "center", gap: 5 }}
                        disabled={restoring === item.id}
                        onClick={() => handleRestore(item)}
                      >
                        <CheckCircleOutlined /> {restoring === item.id ? "..." : t("posPeopleReactivate", "Reactivate")}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="pos-sub-list">
          {list.length === 0 ? (
            <div className="pos-sub-empty">
              <div className="pos-sub-empty-icon">
                {isCustomers ? <ShopOutlined /> : <UserOutlined />}
              </div>
              <div className="pos-sub-empty-title">{emptyTitle}</div>
              <div className="pos-sub-empty-sub">{emptySubtitle}</div>
            </div>
          ) : (
            <div className="pos-sub-cards">
              {list.map((item) => (
                <div
                  className={`pos-sub-card ${isCustomers ? "card-customer" : "card-vendor"}`}
                  key={item.id}
                >
                  <div className="pos-sub-card-avatar">
                    {(item.name || "?")[0].toUpperCase()}
                  </div>
                  <div className="pos-sub-card-info">
                    <div className="pos-sub-card-name">{item.name}</div>
                    {(item.phone_number || item.phone) && (
                      <div className="pos-sub-card-phone">
                        <PhoneOutlined /> {item.phone_number || item.phone}
                      </div>
                    )}
                    <span className="pos-sub-card-type">
                      {isCustomers
                        ? t("customer", "Customer")
                        : t("vendorsColName", "Vendor / Farmer")}
                    </span>
                  </div>
                  <div className="pos-sub-card-actions">
                    <button
                      type="button"
                      className="pos-sub-edit-btn"
                      title={t("history", "History")}
                      onClick={() => isCustomers
                        ? onViewClientStatement && onViewClientStatement(item.id, item.name)
                        : onViewVendorStatement && onViewVendorStatement(item.id, item.name)
                      }
                    >
                      <HistoryOutlined />
                    </button>
                    {canEdit && (
                      <button
                        type="button"
                        className="pos-sub-edit-btn"
                        title={t("edit", "Edit")}
                        onClick={() => openEdit(item)}
                      >
                        <EditOutlined />
                      </button>
                    )}
                    {canDelete && (
                      <button
                        type="button"
                        className="pos-sub-delete-btn"
                        title={t("posPeopleDeactivateOk", "Deactivate")}
                        disabled={deleting === item.id}
                        onClick={() => handleDeactivate(item)}
                      >
                        <StopOutlined />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <Modal
        open={modalOpen}
        onCancel={() => { setModalOpen(false); form.resetFields(); setEditItem(null); }}
        title={
          <span style={{ color: isCustomers ? "#3b82f6" : "#f59e0b" }}>
            {editItem
              ? (isCustomers
                ? t("posPeopleEditCustomer", "Edit Customer")
                : t("posPeopleEditVendor", "Edit Vendor"))
              : (isCustomers
                ? t("posPeopleAddNewCustomer", "Add New Customer")
                : t("posPeopleAddNewVendor", "Add New Vendor / Farmer"))}
          </span>
        }
        onOk={() => form.submit()}
        okText={t("posSave", "Save")}
        cancelText={t("cancel", "Cancel")}
        okButtonProps={{
          style: {
            background: isCustomers ? "#3b82f6" : "#f59e0b",
            borderColor: isCustomers ? "#3b82f6" : "#f59e0b",
            color: "#fff",
          },
        }}
        confirmLoading={saving}
        className="pos-sub-modal"
        width={420}
        zIndex={3000}
      >
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item
            name="name"
            rules={[{ required: true, message: t("nameRequired", "Name is required") }]}
            style={{ marginBottom: 14 }}
          >
            <Input placeholder={t("fullName", "Full name")} className="pos-sub-modal-input" />
          </Form.Item>
          <Form.Item name="phone_number" style={{ marginBottom: 0 }}>
            <Input placeholder={t("phoneOptional", "Phone number (optional)")} className="pos-sub-modal-input" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PosSubPeople;
